import { useEffect, useMemo, useState } from 'react';

/**
 * The default transformer, which does not actually transform
 */
function passObjectWithoutTransformation<P>(response: unknown) {
  return response as P;
}

const REACT_APP_API_ELASTIC_URL = process.env.REACT_APP_API_ELASTIC_URL;
const REACT_APP_API_ELASTIC_AUTH = process.env.REACT_APP_API_ELASTIC_AUTH;
const REACT_APP_API_ELASTIC_INDEX_NAME_SUFFIX = process.env.REACT_APP_API_ELASTIC_INDEX_NAME_SUFFIX;
if (
  !REACT_APP_API_ELASTIC_URL ||
  !REACT_APP_API_ELASTIC_AUTH ||
  !REACT_APP_API_ELASTIC_INDEX_NAME_SUFFIX
) {
  throw new Error('Not enough ElasticSearch environment variables.');
}

/**
 * Make a request to ElasticSearch using fetch(), and return an object with the loading state and response JSON (if
 * available).
 *
 * Query is an object that the hook will serialize for you. As a convenience you can omit useMemo, since a
 * request is only (re)sent if the stringified object changes.
 *
 * If the server response itself should always be transformed before consumption elsewhere, the 2nd
 * hook argument is an optional transform function for your convenience.
 */
export default function useElasticSearch<P>(
  query: Record<string, unknown>,
  transform: (garbage: unknown) => P = passObjectWithoutTransformation
) {
  const [response, setResponse] = useState<P | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const languagePrefix = 'en';
  const url = `${REACT_APP_API_ELASTIC_URL}/${languagePrefix}${REACT_APP_API_ELASTIC_INDEX_NAME_SUFFIX}/_search`;

  const queryString = useMemo(() => JSON.stringify(query), [query]);
  useEffect(() => {
    setIsLoading(true);
    fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        Authorization: `ApiKey ${REACT_APP_API_ELASTIC_AUTH}`,
        'Content-Type': 'application/json',
      },
      body: queryString,
    })
      .then((response) => response.json())
      .then((garbage: unknown) => {
        setIsLoading(false);
        setResponse(transform(garbage));
      });
  }, [url, queryString, transform]);

  return {
    isLoading,
    response,
  };
}
